import i18next from 'i18next';

import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';

import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

interface MessageManagerState {
    duration: number;
    preventClose: boolean;
    onClick: (() => void) | null;

    messageIsError: boolean;
    modalCloseLabel: string;
    onCloseModal: undefined | (() => Promise<boolean>);

    justComponent: boolean;
    onClose: (() => void) | null;

    message: React.ReactNode;
    modalMessage: React.ReactNode;
}

class MessageManager extends SyncExternalStore<MessageManagerState> {
    private autoCloser: number | undefined;

    public close = (): void => {
        window.clearTimeout(this.autoCloser);
        window.removeEventListener('touchstart', this.close);
        window.removeEventListener('mousedown', this.close);
        this.updateState({
            justComponent: false,
            message: null,
            messageIsError: false,
            modalMessage: null,
            onClick: null,
            preventClose: false,
        });
    };

    /**
     *
     * @param message
     * @param duration default 4000
     * @param preventClose default false
     * @param onClick default null
     */
    public showMessage = (
        message: React.ReactNode,
        options?: {
            duration?: number,
            messageIsError?: boolean,
            onClick?: () => void,
            onClose?: () => void,
            preventClose?: boolean,
            hideOnTouchAway?: boolean,
        }
    ): void => {
        // close and resolve previous message
        this.snapshot.onClose?.();

        window.clearTimeout(this.autoCloser);

        if (options?.hideOnTouchAway) {
            window.addEventListener('touchstart', this.close);
            window.addEventListener('mousedown', this.close);
        }

        const duration = options?.duration ?? 4000;

        this.updateState({
            duration,
            justComponent: false,
            message,
            messageIsError: options?.messageIsError ?? false,
            onClick: options?.onClick ?? this.close,
            onClose: options?.onClose ?? this.close,
            preventClose: options?.preventClose ?? false,
        });

        this.autoCloser = window.setTimeout(this.close, duration);
    };

    /**
     * Components/messages shown by this callback will not handle user interactions
     * @param message
     * @param duration
     * @param preventClose
     * @param onClick
     */
    public showComponent = (message: React.ReactNode, duration: number = 4000, preventClose: boolean = false, onClick: (() => void) | null = null): void => {
        window.clearTimeout(this.autoCloser);

        this.updateState({
            duration,
            justComponent: true,
            message,
            modalMessage: null,
            onClick: onClick ?? this.close,
            preventClose,
        });

        this.autoCloser = window.setTimeout(this.close, duration);
    };

    public showModalMessage = (
        modalMessage: React.ReactNode,
        modalCloseLabel?: string,
        onCloseModal?: () => Promise<boolean>
    ): void => {
        window.clearTimeout(this.autoCloser);

        this.updateState({
            justComponent: false,
            modalCloseLabel,
            modalMessage,
            message: null,
            onClick: null,
            onCloseModal,
            preventClose: false,
        });
    };

    public closeModal = async (useOnCloseModal: boolean = true): Promise<void> => {
        window.clearTimeout(this.autoCloser);

        let willClose: boolean = true;

        if (this.snapshot.onCloseModal != null && useOnCloseModal) {
            willClose = await this.snapshot.onCloseModal();
        }

        if (willClose) {
            this.updateState({
                preventClose: false,
                onClick: null,
                modalMessage: null,
            });
        }
    };

    public showErrorMessage(error: unknown): void {
        return this.showMessage(`${i18next.t('Action failed')}${(error instanceof Error && isNotNullOrEmpty(error?.message)) ? ` (${error.message})` : ''}`);
    }
}

export const messageManager = new MessageManager({
    duration: 4000,
    onClick: null,
    modalCloseLabel: '',
    onCloseModal: undefined,
    messageIsError: false,
    preventClose: false,
    justComponent: false,
    onClose: null,
    message: null,
    modalMessage: null,
});
